import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { discoveryFilterStore } from "../../store/filters";
import type {
    DiscoveryFilter,
    DiscoveryFilterField,
} from "../../types/filters/common";
import { encodeFilterStatePayload } from "../../utils/filters.utils";
import { QueryParam } from "../../utils/permission.utils";

const useEncodedFilterStateUrl = (): [() => void, () => string] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsRef = useRef<URLSearchParams>(searchParams);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    const setEncodedFilterStateUrl = useCallback(() => {
        const { contentType } = discoveryFilterStore.getState();
        const { contentSubType, filters } =
            discoveryFilterStore.getState()[contentType];
        const { visibleFilters } = filters[contentSubType.provider];
        const latestFieldSelections =
            filters[contentSubType.provider].fieldSelections;
        const contentTypeToBeEncoded = encodeURIComponent(contentType);
        const contentSubTypeToBeEncoded = encodeURIComponent(
            JSON.stringify(contentSubType),
        );

        const filtersToBeEncoded: Record<
            DiscoveryFilterField,
            DiscoveryFilter
        > = visibleFilters.reduce(
            (acc, { field }) => {
                const selection = latestFieldSelections[field];
                if (selection) {
                    acc[field] = {
                        field,
                        label: selection.label,
                        isAdditional: selection.isAdditional,
                    };
                }
                return acc;
            },
            {} as Record<DiscoveryFilterField, DiscoveryFilter>,
        );

        const payloadToEncode: {
            contentType: string;
            contentSubType: string;
            filters: Record<DiscoveryFilterField, DiscoveryFilter>;
            fieldSelections: any;
            searchQuery?: string;
        } = {
            contentType: contentTypeToBeEncoded,
            contentSubType: contentSubTypeToBeEncoded,
            filters: filtersToBeEncoded,
            fieldSelections: latestFieldSelections,
        };

        payloadToEncode.searchQuery =
            filters[contentSubType.provider].searchQuery;

        const encoded = encodeFilterStatePayload(payloadToEncode);

        if (encoded) {
            const newSearchParams = new URLSearchParams(
                searchParamsRef.current,
            );
            newSearchParams.set(QueryParam.FILTERS, encoded);
            setSearchParams(newSearchParams);
        }
    }, [searchParams, setSearchParams]);

    const getEncodedFilterStateUrl = () => {
        return window.location.href;
    };

    return [setEncodedFilterStateUrl, getEncodedFilterStateUrl];
};

export default useEncodedFilterStateUrl;
